import { useCallback, useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react'
import { useCookies } from 'react-cookie'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import type { FilterTag } from '../../store/sessionFilter/sessionFilter'
import {
  resetSessionFilter,
  selectSessionFilterPage,
  selectSessionFilterPageSize,
  setPage,
  setPageSize,
  setSessionFilterContactIds,
  setSessionFilterDateRange,
  setSessionFilterMeetingKind,
  setSessionFilterMeetingSources,
  setSessionFilterMeetingTemplateIds,
  setSessionFilterTags,
  setSessionFilterUserIds,
} from '../../store/sessionFilter/sessionFilter'
import { useStickySearchParams } from '../../common/useStickySearchParams'
import { STICKY_SESSION_FILTER_SEARCH_PARAMS } from './model'

const DEFAULT_PAGE = 1
const DEFAULT_PAGE_SIZE = 50
const MIN_PAGE_SIZE = 1
const MAX_PAGE_SIZE = 100
const PAGE_PARAM = 'page'
const PAGE_SIZE_PARAM = 'pageSize'
const PAGE_SIZE_COOKIE_KEY = PAGE_SIZE_PARAM
const PAGE_SIZE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365

const FILTER_PARAM_KEYS = ['userId', 'contactId', 'meetingKind', 'meetingSources', 'dateRange', 'meetingTemplates', 'tags'] as const

type UseSessionFiltersArgs = {
  defaultUserIds: string[]
  currentUserId: string
}

type UseSessionFiltersResult = {
  page: number
  pageSize: number
  totalElements: number
  setTotalElements: Dispatch<SetStateAction<number>>
  handlePageChange: (page: number) => void
  handlePageSizeChange: (pageSize: number) => void
  handleFilterChange: (params: URLSearchParams) => void
  resetFilters: () => void
}

const parseNumber = (value: string | number | null | undefined) => {
  if (value == null) {
    return NaN
  }
  const parsed = typeof value === 'number' ? value : parseInt(value, 10)
  return Number.isNaN(parsed) ? NaN : parsed
}

const clampPageSize = (pageSize: number) => {
  if (Number.isNaN(pageSize)) {
    return DEFAULT_PAGE_SIZE
  }
  if (pageSize < MIN_PAGE_SIZE) {
    return MIN_PAGE_SIZE
  }
  if (pageSize > MAX_PAGE_SIZE) {
    return MAX_PAGE_SIZE
  }
  return pageSize
}

const validatePage = (page: number) => {
  if (Number.isNaN(page) || page < DEFAULT_PAGE) {
    return DEFAULT_PAGE
  }
  return Math.floor(page)
}

const getJSON = <T>(value: string | null, fallback: T) => {
  if (!value) {
    return fallback
  }
  try {
    return JSON.parse(value) as T
  } catch {
    return fallback
  }
}

export const useSessionFilters = ({ defaultUserIds, currentUserId }: UseSessionFiltersArgs): UseSessionFiltersResult => {
  const dispatch = useAppDispatch()

  const { searchParams, setSearchParams } = useStickySearchParams(STICKY_SESSION_FILTER_SEARCH_PARAMS)

  const [cookies, setCookie] = useCookies([PAGE_SIZE_COOKIE_KEY])
  const pageFromStore = useAppSelector(selectSessionFilterPage)
  const pageSizeFromStore = useAppSelector(selectSessionFilterPageSize)

  const cookiePageSize = useMemo(() => {
    const rawCookieValue = parseNumber(cookies[PAGE_SIZE_COOKIE_KEY])
    return Number.isNaN(rawCookieValue) ? null : clampPageSize(rawCookieValue)
  }, [cookies])

  const page = pageFromStore ? validatePage(pageFromStore) : DEFAULT_PAGE
  const pageSize = pageSizeFromStore ? clampPageSize(pageSizeFromStore) : (cookiePageSize ?? DEFAULT_PAGE_SIZE)
  const [totalElements, setTotalElements] = useState<number>(0)

  const replaceSearchParams = useCallback(
    (next: URLSearchParams) => {
      if (next.toString() === searchParams.toString()) {
        // Safari throws SecurityError if replaceState is spammed with identical payloads
        return
      }

      setSearchParams(next, { replace: true })
    },
    [searchParams, setSearchParams],
  )

  const mutateSearchParams = useCallback(
    (mutator: (params: URLSearchParams) => void) => {
      const current = new URLSearchParams(searchParams)
      const currentSnapshot = current.toString()

      mutator(current)

      if (current.toString() === currentSnapshot) {
        // Avoid triggering a history.replaceState no-op which Safari rate-limits
        return
      }

      setSearchParams(current, { replace: true })
    },
    [searchParams, setSearchParams],
  )

  useEffect(() => {
    const hasPageParam = searchParams.has(PAGE_PARAM)
    const rawPageParam = parseNumber(searchParams.get(PAGE_PARAM))
    const pageFromUrl = validatePage(rawPageParam)
    const fallbackPage = pageFromStore ? validatePage(pageFromStore) : DEFAULT_PAGE
    const nextPage = hasPageParam ? pageFromUrl : fallbackPage

    if (pageFromStore !== nextPage) {
      dispatch(setPage(nextPage))
    }

    const hasPageSizeParam = searchParams.has(PAGE_SIZE_PARAM)
    const rawPageSizeParam = parseNumber(searchParams.get(PAGE_SIZE_PARAM))
    const pageSizeFromUrl = clampPageSize(rawPageSizeParam)
    const fallbackPageSize = pageSizeFromStore ?? cookiePageSize ?? DEFAULT_PAGE_SIZE
    const nextPageSize = hasPageSizeParam ? pageSizeFromUrl : fallbackPageSize
    if (pageSizeFromStore !== nextPageSize) {
      dispatch(setPageSize(nextPageSize))
    }

    if (!hasPageParam || !hasPageSizeParam) {
      mutateSearchParams((params) => {
        if (!hasPageParam) {
          params.set(PAGE_PARAM, nextPage.toString())
        }
        if (!hasPageSizeParam) {
          params.set(PAGE_SIZE_PARAM, nextPageSize.toString())
        }
      })
      return
    }

    const hasParams = Array.from(searchParams).length > 0
    if (!hasParams) {
      return
    }

    const filterId = searchParams.get('filterId')
    if (filterId) {
      let paramsChanged = false
      const nextParams = new URLSearchParams(searchParams)
      FILTER_PARAM_KEYS.forEach((key) => {
        if (nextParams.has(key)) {
          nextParams.delete(key)
          paramsChanged = true
        }
      })

      if (paramsChanged) {
        replaceSearchParams(nextParams)
      }
      return
    }

    const userIdParam = searchParams.get('userId')
    if (userIdParam) {
      dispatch(setSessionFilterUserIds(userIdParam.split(',')))
    }

    const contactIdParam = searchParams.get('contactId')
    if (contactIdParam) {
      dispatch(setSessionFilterContactIds(contactIdParam.split(',')))
    }

    const meetingKindParam = searchParams.get('meetingKind')
    if (meetingKindParam) {
      dispatch(setSessionFilterMeetingKind(meetingKindParam))
    }

    const meetingSources = getJSON<string[]>(searchParams.get('meetingSources'), [])
    dispatch(setSessionFilterMeetingSources(meetingSources))

    const dateRange = getJSON<[string, string] | null>(searchParams.get('dateRange'), null)
    dispatch(setSessionFilterDateRange(dateRange))

    const meetingTemplates = getJSON<string[]>(searchParams.get('meetingTemplates'), [])
    dispatch(setSessionFilterMeetingTemplateIds(meetingTemplates))

    const tags = getJSON<FilterTag[]>(searchParams.get('tags'), [])
    if (tags.length > 0) {
      dispatch(setSessionFilterTags(tags))
    }
  }, [cookiePageSize, dispatch, mutateSearchParams, pageFromStore, pageSizeFromStore, replaceSearchParams, searchParams])

  const handlePageChange = useCallback(
    (requestedPage: number) => {
      const nextPage = validatePage(requestedPage)
      dispatch(setPage(nextPage))
      mutateSearchParams((params) => {
        params.set(PAGE_PARAM, nextPage.toString())
        params.set(PAGE_SIZE_PARAM, pageSize.toString())
      })
    },
    [dispatch, mutateSearchParams, pageSize],
  )

  const handlePageSizeChange = useCallback(
    (requestedPageSize: number) => {
      if (!requestedPageSize) {
        return
      }
      const nextPageSize = clampPageSize(requestedPageSize)
      setCookie(PAGE_SIZE_COOKIE_KEY, nextPageSize, { path: '/', maxAge: PAGE_SIZE_COOKIE_MAX_AGE })

      const nextPage = DEFAULT_PAGE
      dispatch(setPage(nextPage))
      dispatch(setPageSize(nextPageSize))

      mutateSearchParams((params) => {
        params.set(PAGE_PARAM, nextPage.toString())
        params.set(PAGE_SIZE_PARAM, nextPageSize.toString())
      })
    },
    [dispatch, mutateSearchParams, setCookie],
  )

  const handleFilterChange = useCallback(
    (params: URLSearchParams) => {
      const nextParams = new URLSearchParams(params)
      nextParams.delete(PAGE_PARAM)
      nextParams.delete(PAGE_SIZE_PARAM)

      if (!nextParams.has('userId') && !nextParams.has('filterId')) {
        nextParams.set('userId', currentUserId)
        dispatch(setSessionFilterUserIds(defaultUserIds))
      }

      const nextPage = DEFAULT_PAGE
      const retainedPageSize = pageSizeFromStore ?? cookiePageSize ?? DEFAULT_PAGE_SIZE
      dispatch(setPage(nextPage))
      dispatch(setPageSize(retainedPageSize))
      nextParams.set(PAGE_PARAM, nextPage.toString())
      nextParams.set(PAGE_SIZE_PARAM, retainedPageSize.toString())
      replaceSearchParams(nextParams)
    },
    [cookiePageSize, currentUserId, defaultUserIds, dispatch, pageSizeFromStore, replaceSearchParams],
  )

  const resetFilters = useCallback(() => {
    dispatch(resetSessionFilter(defaultUserIds))
    const params = new URLSearchParams()
    params.set('userId', currentUserId)
    handleFilterChange(params)
  }, [currentUserId, defaultUserIds, dispatch, handleFilterChange])

  return {
    page,
    pageSize,
    totalElements,
    setTotalElements,
    handlePageChange,
    handlePageSizeChange,
    handleFilterChange,
    resetFilters,
  }
}
